import { useEffect, useState, useSyncExternalStore } from "react";
import { motionTokens } from "./tokens";

const viewportListeners = new Set();
let viewportSize = readViewportSize();
let removeViewportListener;

function readViewportSize() {
  if (typeof window === "undefined") return "0x0";
  return `${window.innerWidth}x${window.innerHeight}`;
}

function updateViewportSize() {
  const nextViewportSize = readViewportSize();
  if (nextViewportSize === viewportSize) return;
  viewportSize = nextViewportSize;
  viewportListeners.forEach((listener) => listener());
}

function subscribeToViewport(listener) {
  if (viewportListeners.size === 0) viewportSize = readViewportSize();
  viewportListeners.add(listener);
  if (viewportListeners.size === 1 && typeof window !== "undefined") {
    window.addEventListener("resize", updateViewportSize, { passive: true });
    removeViewportListener = () => window.removeEventListener("resize", updateViewportSize);
  }

  return () => {
    viewportListeners.delete(listener);
    if (viewportListeners.size === 0 && removeViewportListener) {
      removeViewportListener();
      removeViewportListener = undefined;
    }
  };
}

function getViewportSize() {
  return viewportSize;
}

function getServerViewportSize() {
  return "0x0";
}

export function useMediaQuery(query) {
  const getMatches = () => (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false
  );
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;

    const mediaQuery = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);
    setMatches(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [query]);

  return matches;
}

export function useLandingMobile() {
  return useMediaQuery(`(max-width: ${motionTokens.breakpoint.mobile}px)`);
}

export function useLandingViewport() {
  const size = useSyncExternalStore(subscribeToViewport, getViewportSize, getServerViewportSize);
  const [width, height] = size.split("x").map(Number);
  const policy = width <= motionTokens.breakpoint.mobile
    ? motionTokens.viewport.mobile
    : motionTokens.viewport.desktop;
  const bottomInset = Math.round(height * (1 - policy.triggerRatio));

  return {
    amount: policy.amount,
    margin: `0px 0px -${bottomInset}px 0px`,
    triggerRatio: policy.triggerRatio,
  };
}
